import { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import type { PromptModeInstance } from "@/stores/admin/models";

// Classifier sections config
const CLASSIFIER_SECTIONS = [
  {
    slug: "folder_category",
    title: "Document Category",
    description: "Classifies documents into categories",
    variables: {
      text: ["{categories_list}"],
      image: ["{categories_list}"],
    },
  },
  {
    slug: "folder_subcategory",
    title: "Document Subcategory",
    description: "Refines classification into subcategories",
    variables: {
      text: ["{parent_category}", "{subcategories_list}"],
      image: ["{subcategories_list}"],
    },
  },
  {
    slug: "project_stage",
    title: "Project Stage",
    description: "Classifies documents into construction project stages",
    variables: {
      text: [],
      text_specification: [],
      image: [],
      image_specification: [],
    },
  },
  {
    slug: "construction_parts",
    title: "Design Discipline",
    description: "Tags documents with design discipline tags",
    hasPromptAddition: true,
    variables: {
      text: ["{reference_categories}", "{abbreviations_context}", "{prompt_addition}"],
      image: ["{reference_categories}", "{abbreviations_context}", "{prompt_addition}"],
    },
  },
] as const;

type VariablesMap = Record<string, string[]>;

// Per-mode editor with dirty tracking
const ModeEditor = observer(
  ({
    mode,
    variables,
    onSave,
  }: {
    mode: PromptModeInstance;
    variables: string[];
    onSave: (id: number, value: string) => Promise<void>;
  }) => {
    const [value, setValue] = useState(mode.system_prompt);
    const [isSaving, setIsSaving] = useState(false);
    const hasChanges = value !== mode.system_prompt;

    useEffect(() => {
      setValue(mode.system_prompt);
    }, [mode.system_prompt]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        await onSave(mode.id, value);
        toast.success(`${mode.mode} prompt updated`);
      } catch {
        toast.error("Failed to update prompt");
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="space-y-3">
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={14}
          className="font-mono text-sm"
        />
        {variables.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Variables:</span>
            {variables.map((v) => (
              <Badge
                key={v}
                variant="secondary"
                className="border border-blue-200 bg-blue-50 font-mono text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
              >
                {v}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </div>
      </div>
    );
  }
);

// Prompt Addition editor for construction_tagger (existing behavior)
const PromptAdditionEditor = observer(() => {
  const { promptsStore } = useStores();
  const prompt = promptsStore.getBySlug("construction_tagger");
  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const hasChanges = value !== (prompt?.prompt_addition ?? "");

  useEffect(() => {
    if (prompt) {
      setValue(prompt.prompt_addition ?? "");
    }
  }, [prompt?.id, prompt?.prompt_addition]);

  const handleSave = async () => {
    if (!prompt) return;
    setIsSaving(true);
    try {
      await promptsStore.updatePrompt(prompt.id, {
        prompt_addition: value,
      });
      toast.success("Prompt addition updated");
    } catch {
      toast.error("Failed to update prompt addition");
    } finally {
      setIsSaving(false);
    }
  };

  if (!prompt) return null;

  return (
    <div className="mt-2 space-y-3 border-t pt-4">
      <div>
        <p className="mb-1 text-sm font-medium">Prompt Addition</p>
        <p className="mb-2 text-xs text-muted-foreground">
          Extra rules appended to the system prompt via{" "}
          <code className="rounded bg-muted px-1">{"{prompt_addition}"}</code>
        </p>
      </div>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={6}
        className="font-mono text-sm"
        placeholder="Enter tag-level specific rules..."
      />
      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Changes
        </Button>
      </div>
    </div>
  );
});

// Classifier section with collapsible + tabs
const ClassifierSection = observer(
  ({
    slug,
    title,
    description,
    hasPromptAddition,
    variables,
    onSaveMode,
  }: {
    slug: string;
    title: string;
    description: string;
    hasPromptAddition?: boolean;
    variables: VariablesMap;
    onSaveMode: (id: number, value: string) => Promise<void>;
  }) => {
    const { promptsStore } = useStores();
    const [open, setOpen] = useState(false);
    const modes = promptsStore.getModesForSlug(slug);

    if (modes.length === 0) return null;

    return (
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border px-4 py-3 text-left hover:bg-muted/50">
          <div>
            <p className="font-medium">{title}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-1 pt-3">
          <Tabs defaultValue={modes[0].mode}>
            <TabsList>
              {modes.map((m) => (
                <TabsTrigger key={m.mode} value={m.mode}>
                  {m.mode.replace(/_/g, " ")}
                </TabsTrigger>
              ))}
            </TabsList>
            {modes.map((m) => (
              <TabsContent key={m.mode} value={m.mode}>
                <ModeEditor mode={m} variables={variables[m.mode] ?? []} onSave={onSaveMode} />
              </TabsContent>
            ))}
          </Tabs>
          {hasPromptAddition && <PromptAdditionEditor />}
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

const PromptsManagement = observer(() => {
  const { promptsStore } = useStores();

  useEffect(() => {
    promptsStore.loadPrompts();
  }, [promptsStore]);

  const handleSaveMode = useCallback(
    async (id: number, value: string) => {
      await promptsStore.updatePromptMode(id, value);
    },
    [promptsStore]
  );

  if (promptsStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (promptsStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading prompts: {promptsStore.error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {CLASSIFIER_SECTIONS.map((section) => (
        <ClassifierSection
          key={section.slug}
          slug={section.slug}
          title={section.title}
          description={section.description}
          hasPromptAddition={"hasPromptAddition" in section ? section.hasPromptAddition : undefined}
          variables={section.variables as unknown as VariablesMap}
          onSaveMode={handleSaveMode}
        />
      ))}
    </div>
  );
});

export default PromptsManagement;
